import { useCallback, useEffect, useRef, useState } from 'react';
import {
  SetupInvitationError,
  getSetupInvitationPreview,
} from '../../services/setupInvitations';
import type {
  AcceptedSetupInvitation,
  SetupInvitationPreview,
} from '../../types/setupInvitations';

/**
 * The setup-program page's state vocabulary, and the hook that loads the preview.
 *
 * Same shape as `useInvitationPreview`: the page renders `outcome ?? status`, so a submission result
 * replaces the preview rather than coexisting with it.
 */

export type SetupPreviewStatus =
  | { phase: 'loading' }
  /** No token in the fragment, or the server refused it. One state, on purpose. */
  | { phase: 'invalid' }
  /** The server could not answer. No retry for a rate limit, which would spend an attempt. */
  | { phase: 'unavailable'; message: string; canRetry: boolean }
  | { phase: 'form'; preview: SetupInvitationPreview };

/** How a submission ended, when it ended the page rather than leaving the form up. */
export type SetupProgramOutcome =
  | { phase: 'accepted'; accepted: AcceptedSetupInvitation }
  | { phase: 'registered'; message: string; email: string }
  | { phase: 'invalid' };

const statusForError = (error: unknown): SetupPreviewStatus => {
  if (
    error instanceof SetupInvitationError &&
    (error.kind === 'unavailable' || error.kind === 'rate_limited')
  ) {
    return {
      phase: 'unavailable',
      message: error.message,
      canRetry: error.kind !== 'rate_limited',
    };
  }

  return { phase: 'invalid' };
};

/**
 * Loads the preview. The request counter matters: StrictMode runs this effect twice in
 * development, and the slower answer must not overwrite the newer one.
 */
export const useSetupInvitationPreview = (token: string | null) => {
  const [status, setStatus] = useState<SetupPreviewStatus>(() =>
    token ? { phase: 'loading' } : { phase: 'invalid' }
  );
  const requestIdRef = useRef(0);

  const reload = useCallback(async () => {
    if (!token) return;

    const requestId = ++requestIdRef.current;
    setStatus({ phase: 'loading' });

    try {
      const preview = await getSetupInvitationPreview(token);
      if (requestIdRef.current === requestId) setStatus({ phase: 'form', preview });
    } catch (error: unknown) {
      if (requestIdRef.current === requestId) setStatus(statusForError(error));
    }
  }, [token]);

  useEffect(() => {
    void reload();
    return () => {
      requestIdRef.current += 1;
    };
  }, [reload]);

  return { status, reload };
};
